import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    TextField,
    Avatar,
    InputAdornment,
    CircularProgress,
    DialogActions,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import RELogo from '../../../../../Assets/AuthBackGround/RecEzyLogo.png';
import { ArrowUpward } from "@mui/icons-material";
import { theme } from "../../../../../theme";
import { postJsonRequest } from "../../../../../service/apiService";
import { useParams } from "react-router-dom";
import { apiUrl } from "../../../../../Constant/apiConstant";
import { useSnackbar } from "../../../../../Context/SnackbarContext";

const EkoAssistDialog = ({ open, onClose, setJobDescription }: { open: boolean; onClose: () => void, setJobDescription: (value: string) => void }) => {
    const [prompt, setPrompt] = useState("");
    const [loading, setLoading] = useState(false);
    const [copiedText, setCopiedText] = useState("");
    const [conversation, setConversation] = useState<
        { role: "user" | "assistant"; content: string }[]
    >([]);
    const { tenant } = useParams<{ tenant: string }>();
    const { showSnackbar } = useSnackbar();

    // Helper: Converts plain text with \n into HTML with <br />
    const convertNewlinesToHTML = (text: string) => {
        return text.split('\n').map(line => line.trim()).join('<br/>');
    };


    const handleSubmit = async () => {
        if (!prompt.trim()) return;

        setLoading(true);
        setConversation((prev) => [...prev, { role: "user", content: prompt }]);
        setPrompt("");

        try {
            const response = await postJsonRequest(apiUrl.JM_GET_GENERATE_JOB_DESCRIPTION,
                {
                    prompt,
                    conversation
                },
                tenant
            )
            if (response.status === 200) {
                const { Status, JobDescription } = response.data;

                if (Status) {
                    const htmlContent = convertNewlinesToHTML(JobDescription);

                    setJobDescription(htmlContent);
                    setConversation((prev) => [...prev, { role: "assistant", content: JobDescription }]);
                } else {
                    showSnackbar("Failed to generate job description. Please try again.", "error");
                    setConversation((prev) => [
                        ...prev,
                        { role: "assistant", content: "Failed to generate job description. Please try again." },
                    ]);
                }
            }
        } catch (error) {
            console.error("OpenAI Error:", error);
            showSnackbar("Oops! We couldn't generate the job description right now. Please wait a moment and try again.", "error");
            setConversation((prev) => [
                ...prev,
                { role: "assistant", content: "Oops! We couldn't generate the job description right now. Please wait a moment and try again." },
            ]);
        }

        setLoading(false);
        onClose();
    };

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopiedText(text);
        setTimeout(() => setCopiedText(""), 2000);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth >
            <DialogTitle
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{display:"flex",flexDirection:'column'}}>
                    <Typography variant="h6" fontWeight={600}>
                        Eko Assist
                    </Typography>
                    <Typography variant="subtitle1">
                        Your AI assistant for crafting precise and tailored job descriptions
                    </Typography>
                </Box>


                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {/* All conversation messages */}
                {conversation.map((msg, index) => (
                    <Box
                        key={index}
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                        mb={2}
                        flexDirection="row"
                    >
                        {msg.role === "user" ? (
                            <Avatar
                                sx={{
                                    bgcolor: "#E0F7FA",
                                    color: "#00796B",
                                    fontSize: 14,
                                    width: 24,
                                    height: 24,
                                }}
                            >
                                Y
                            </Avatar>
                        ) : (
                            <Avatar
                                src={RELogo}
                                sx={{ width: '1.8vw', height: '1.8vw', mr: 1 }}
                            />
                        )}


                        <Box sx={{ width: "100%" }}>
                            {msg.role === "user" && (
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    You
                                </Typography>
                            )}
                            <Box sx={{ background: "#F9FAFB", p: 2, borderRadius: 2 }}>
                                {msg.role === "assistant" && (
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="subtitle2">Eko</Typography>
                                        <IconButton size="small" onClick={() => handleCopy(msg.content)}>
                                            {copiedText === msg.content ? (
                                                <CheckIcon fontSize="small" color="success" />
                                            ) : (
                                                <ContentCopyIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </Box>
                                )}
                                <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                                    {msg.content}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                ))}


                {/* Loader while waiting for response */}
                {loading && (
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Avatar src="/eko-bot-icon.png" alt="Eko" sx={{ bgcolor: "#EEF2FF", width: 24, height: 24 }}>
                            E
                        </Avatar>
                        <Box sx={{ background: "#F9FAFB", p: 2, borderRadius: 2, width: "100%" }}>
                            <Typography variant="subtitle2" mb={1}>
                                Eko is preparing your response...
                            </Typography>
                            <CircularProgress size={20} />
                        </Box>
                    </Box>
                )}

            </DialogContent>

            <DialogActions sx={{
                flexDirection: "column", alignItems: "stretch", px: 3,
                py: 1,
            }}>
                <TextField
                    size="small"
                    label="Your Prompt"
                    fullWidth
                    multiline
                    rows={4}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Write a job description or request based on the job role..."
                    variant="outlined"
                    sx={{
                        "& textarea": { resize: "none" },
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={handleSubmit}
                                    disabled={loading || !prompt}
                                    sx={{
                                        backgroundColor: theme.palette.primary.main, // Tailwind's purple-500
                                        borderRadius: '12px',       // Make it rounded square
                                        width: 40,
                                        height: 40,
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#7C3AED', // Slightly darker on hover
                                        },
                                    }}
                                >
                                    <ArrowUpward />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <Typography
                    variant="caption"
                    sx={{ mt: 1, color: "gray", display: "block", textAlign: "center" }}
                >
                    Eko can make mistakes. Consider checking important information.
                </Typography>
            </DialogActions>
        </Dialog>
    );
};

export default EkoAssistDialog;
